import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Button, Flex, Skeleton, Switch, useToast } from '@chakra-ui/react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { breadcrumb } from './breadcrumb';
import { PollsFilters } from './types';
import { Polls, PollsInteractivity } from '@/model';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useAppApplicationComponentArea } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { useDeletePollsMutation, useGetPolls, useGetPollsById, usePutPollsInteractiveMutation } from '@/services/queries/polls';
import { PollsFiltersBox } from './components/PollsFiltersBox';
import { PollsTable } from './components/PollsTable';
import { PollsCreateModal } from './components/PollsCreateModal';
import { PollsEditModal } from './components/PollsEditModal';
import { PollsResultDetailModal } from './components/PollsResultDetailModal';
import { queryClient } from '@/services/api/queryClient';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationPolls = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.POLLS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Polls | undefined>(undefined);
  const [isResultDetailModalOpen, setResultDetailModalOpen] = useState(false);
  const [resultDetailItem, setResultDetailItem] = useState<Polls | undefined>(undefined);

  const interactiveMutation = usePutPollsInteractiveMutation();
  const deleteMutation = useDeletePollsMutation();
  const getItemByUUID = useGetPollsById();
  const getResultByUUID = useGetPollsById();

  const filtersForm = useFiltersForm<PollsFilters>({
    defaultValues: {
      team_uuid: '',
      question: '',
      status: '',
      dateValue: DateFilterInputValue.ALL,
      start_date: '',
      end_date: '',
      interactivity: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useGetPolls(params);

  const itemsData = itemData?.data || [];

  const { data: clubs = [] } = useAllClub(true);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_polls.application_polls_enable_success')
      : t('pages.application_polls.application_polls_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_polls.application_polls_enable_error')
      : t('pages.application_polls.application_polls_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.POLLS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const onHandleDelete = (item: Polls) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_polls.application_polls_not_found_error'),
        });
      },
    });
  };

  const onHandleViewResult = (item: Polls) => {
    setResultDetailModalOpen(true);
    setResultDetailItem(undefined);

    getResultByUUID.mutate(item.uuid, {
      onSuccess: res => {
        setResultDetailItem(res);
      },
      onError: (resp: any) => {
        setResultDetailModalOpen(false);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_polls.application_polls_result_error'),
        });
      },
    });
  };

  const cancelResultDetailModal = () => {
    setResultDetailModalOpen(false);
    setResultDetailItem(undefined);
  };

  const handleFinish = () => {
    if (!selectedItem?.uuid) return;

    const payload = {
      interactivity: PollsInteractivity.CLOSED,
    };

    interactiveMutation.mutate(
      {
        id: selectedItem?.uuid,
        payload,
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_polls.application_polls_finish_success'),
            status: 'success',
          });

          queryClient.invalidateQueries(['GET_POLLS', params]);
          setFinishModalOpen(false);
          setSelectedItem(undefined);
        },
        onError: (e: any) => {
          toast({
            title: e?.message || t('pages.application_polls.application_polls_finish_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_polls.application_polls_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (e: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: e?.message || t('pages.application_polls.application_polls_delete_error'),
        });
      },
    });
  };

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent
      title={t('pages.application_polls.application_polls_page_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          display='flex'
          alignItems='center'
          gap={2}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.application_polls.application_polls_create_button')}
        </Button>
      }
    >
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_polls.application_polls_section_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_polls.application_polls_switch_enabled')
                    : t('pages.application_polls.application_polls_switch_disabled')}
                </Switch>
              </>
            )}
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <PollsFiltersBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <PollsTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onDeleteClick={item => onHandleDelete(item)}
            onFinishClick={item => {
              setSelectedItem(item);
              setFinishModalOpen(true);
            }}
            onViewResultClick={onHandleViewResult}
            onEditClick={item => onHandleEdit(item.uuid)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && isEditModalOpen ? (
        <PollsEditModal
          item={selectedItem}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onDone={() => {
            setIsEditModalOpen(false);
            queryClient.invalidateQueries(['GET_POLLS', params]);
          }}
        />
      ) : null}

      <PollsCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={finishModalOpen}
        withoutCloseButton
        onClose={() => {
          setFinishModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={handleFinish}
        title={t('pages.application_polls.application_polls_finish_modal_title')}
        description={t('pages.application_polls.application_polls_finish_modal_description')}
        isLoading={deleteMutation.isLoading}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_polls.application_polls_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_polls.application_polls_delete_modal_description_prefix')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.identifier}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_polls.application_polls_toggle_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_polls.application_polls_enable_modal_description')
              : t('pages.application_polls.application_polls_disable_modal_description')}
          </Text>
        }
      />

      <PollsResultDetailModal
        isOpen={isResultDetailModalOpen}
        isLoading={getResultByUUID.isLoading}
        item={resultDetailItem}
        onClose={cancelResultDetailModal}
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
